import { Controller } from "@hotwired/stimulus";

const STORAGE_PREFIX = "form_draft_";
const EXPIRY_SWEEP_INTERVAL_MS = 5 * 60 * 1000;
const LEGACY_CASE_NOTE_KEY = /^form_draft_case_note_\d+_comment$/;

export function purgeExpiredFormDrafts(now = Date.now()): void {
  try {
    const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      .filter((key): key is string => Boolean(key?.startsWith(STORAGE_PREFIX)));

    keys.forEach((key) => {
      try {
        // Pre-TTL case-note drafts were keyed only by case-note id. They
        // cannot be attributed safely after the org/user-scoped migration.
        if (LEGACY_CASE_NOTE_KEY.test(key)) {
          localStorage.removeItem(key);
          return;
        }
        const expiresAt = JSON.parse(localStorage.getItem(key) || "null")?.expiresAt;
        if (typeof expiresAt === "number" && Number.isFinite(expiresAt) && expiresAt <= now) {
          localStorage.removeItem(key);
        }
      } catch {
        // The owning form clears corrupt entries if it is opened.
      }
    });
  } catch {
    // Storage may be unavailable in private or locked-down browser contexts.
  }
}

// Stimulus controller modules load at application startup, so sensitive
// drafts expire even when their original case-note form is never revisited.
purgeExpiredFormDrafts();

// Reusable localStorage draft persistence for any form.
//
//   <form data-controller="form-draft"
//         data-form-draft-key-value="support_reply_12">
//     <textarea data-form-draft-target="field"
//               data-action="input->form-draft#save"></textarea>
//   </form>
//
// Contract:
// - Restores on connect, but only into EMPTY fields — a server-rendered value
//   (the chat-escalation form arrives prefilled) always wins over a stale
//   local draft.
// - Clears on a successful turbo:submit-end.
// - `clear` value: a re-render that follows a successful send OUTSIDE Turbo's
//   submit lifecycle (the attachment reply arrives as a stream replace from a
//   raw fetch) sets it so the fresh form clears the draft instead of
//   resurrecting the text that was just sent.
// - Fields may be textarea/input or contenteditable. Restores dispatch a
//   bubbling `input` (deferred a tick) so companion controllers —
//   mention-contenteditable's hidden-field sync, case-note-form's autosize
//   and submit state — see the restored value.
// - `checkbox` targets persist as a group: the checked values save together
//   and restore only when NO box in the group is checked, the checkbox
//   analog of "server value wins". Restores dispatch bubbling `change`.
//
// This is deliberately persistence-only — surfaces compose it with their own
// UI controllers (support composers with mention/dropzone, case notes with
// case-note-form).
export default class extends Controller<HTMLElement> {
  static targets = ["field", "checkbox"];
  static values = {
    key: String,
    clear: { type: Boolean, default: false },
    ttlHours: { type: Number, default: 0 },
  };

  declare readonly fieldTargets: HTMLElement[];
  declare readonly checkboxTargets: HTMLInputElement[];
  declare readonly hasCheckboxTarget: boolean;
  declare keyValue: string;
  declare clearValue: boolean;
  declare ttlHoursValue: number;
  private expirySweepTimer: number | null = null;

  connect(): void {
    purgeExpiredFormDrafts();
    if (this.clearValue) {
      this.clearStorage();
    } else {
      this.restore();
    }
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd);
    if (this.ttlHoursValue > 0) {
      this.expirySweepTimer = window.setInterval(
        () => purgeExpiredFormDrafts(),
        EXPIRY_SWEEP_INTERVAL_MS
      );
    }
  }

  disconnect(): void {
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd);
    if (this.expirySweepTimer !== null) window.clearInterval(this.expirySweepTimer);
    this.expirySweepTimer = null;
  }

  save(): void {
    try {
      const fields: Record<string, string> = {};
      this.fieldTargets.forEach((field, index) => {
        fields[this.fieldKey(field, index)] = this.read(field);
      });
      const checked = this.checkboxTargets.filter((cb) => cb.checked).map((cb) => cb.value);
      const savedAt = Date.now();
      const expiresAt = this.ttlHoursValue > 0
        ? savedAt + this.ttlHoursValue * 60 * 60 * 1000
        : undefined;
      localStorage.setItem(this.storageKey, JSON.stringify({ fields, checked, savedAt, expiresAt }));
    } catch {
      // Storage full or unavailable — drafting is best-effort by design.
    }
  }

  clearStorage(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // Ignore: nothing to clear or storage unavailable.
    }
  }

  private onSubmitEnd = (event: Event): void => {
    if ((event as CustomEvent).detail?.success) this.clearStorage();
  };

  private restore(): void {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;

      const parsed = JSON.parse(raw) || {};
      if (this.expired(parsed.savedAt, parsed.expiresAt)) {
        this.clearStorage();
        return;
      }

      const fields: Record<string, string> = parsed.fields || {};
      this.fieldTargets.forEach((field, index) => {
        const saved = fields[this.fieldKey(field, index)];
        if (saved && this.read(field).trim() === "") this.write(field, saved);
      });
      this.restoreCheckboxes(Array.isArray(parsed.checked) ? parsed.checked : []);
    } catch {
      // A corrupt entry is worth less than a working form.
      this.clearStorage();
    }
  }

  // Group restore, and only into an all-unchecked group — a server-checked
  // box is the checkbox analog of a prefilled field.
  private restoreCheckboxes(checked: string[]): void {
    if (!this.hasCheckboxTarget || checked.length === 0) return;
    if (this.checkboxTargets.some((cb) => cb.checked)) return;

    const restored = this.checkboxTargets.filter((cb) => checked.includes(cb.value));
    restored.forEach((cb) => {
      cb.checked = true;
    });
    // Deferred like the field dispatch below, and `change` because that is
    // what a real click produces — companion controllers re-render from it.
    setTimeout(() => {
      restored.forEach((cb) => cb.dispatchEvent(new Event("change", { bubbles: true })));
    }, 0);
  }

  private read(field: HTMLElement): string {
    return this.editable(field) ? field.innerHTML : (field as HTMLInputElement).value ?? "";
  }

  private write(field: HTMLElement, text: string): void {
    if (this.editable(field)) {
      // The draft is this browser's own prior input, round-tripped — the same
      // trust level as the live editor content it came from.
      field.innerHTML = text;
    } else {
      (field as HTMLInputElement).value = text;
    }
    // Deferred a tick: companion controllers (mention-contenteditable's
    // hidden-field sync, case-note-form's autosize/submit state) connect
    // after this one in the same batch, so a synchronous event fires before
    // anyone listens — and the restored value would go unnoticed.
    setTimeout(() => field.dispatchEvent(new Event("input", { bubbles: true })), 0);
  }

  // Attribute check as well as the property: jsdom never sets
  // isContentEditable, and the attribute is what the templates declare anyway.
  private editable(field: HTMLElement): boolean {
    return field.isContentEditable || field.getAttribute("contenteditable") === "true";
  }

  private fieldKey(field: HTMLElement, index: number): string {
    return (field as HTMLInputElement).name || field.id || String(index);
  }

  private expired(savedAt: unknown, expiresAt: unknown): boolean {
    if (this.ttlHoursValue <= 0) return false;
    if (typeof expiresAt === "number" && Number.isFinite(expiresAt)) return expiresAt <= Date.now();
    if (typeof savedAt !== "number" || !Number.isFinite(savedAt)) return true;

    return Date.now() - savedAt > this.ttlHoursValue * 60 * 60 * 1000;
  }

  private get storageKey(): string {
    return `${STORAGE_PREFIX}${this.keyValue}`;
  }
}
